/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import {AnimationBuilder, AnimationFactory, AnimationMetadata, AnimationOptions, AnimationPlayer, NoopAnimationPlayer, sequence} from '@angular/animations';
import {Inject, Injectable, RendererFactory2, RendererType2, ViewEncapsulation} from '@angular/core';
import {DOCUMENT} from '@angular/platform-browser';

import {AnimationRenderer} from './animation_renderer';
export class BrowserAnimationBuilder extends AnimationBuilder {
private _nextAnimationId = 0;
private _renderer: AnimationRenderer;
/**
 * @param {?} rootRenderer
 * @param {?} doc
 */
constructor(rootRenderer: RendererFactory2,  doc: any) {
    super();
    const typeData = {
      id: '0',
      encapsulation: ViewEncapsulation.None,
      styles: [],
      data: {animation: []}
    } as RendererType2;
    this._renderer = rootRenderer.createRenderer(doc.body, typeData) as AnimationRenderer;
  }
/**
 * @param {?} animation
 * @return {?}
 */
build(animation: AnimationMetadata|AnimationMetadata[]): AnimationFactory {
    const /** @type {?} */ id = this._nextAnimationId.toString();
    this._nextAnimationId++;
    const /** @type {?} */ entry = Array.isArray(animation) ? sequence(animation) : animation;
    issueAnimationCommand(this._renderer, null, id, 'register', [entry]);
    return new BrowserAnimationFactory(id, this._renderer);
  }
static decorators: DecoratorInvocation[] = [
{ type: Injectable },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: RendererFactory2, },
{type: undefined, decorators: [{ type: Inject, args: [DOCUMENT, ] }, ]},
];
}

function BrowserAnimationBuilder_tsickle_Closure_declarations() {
/** @type {?} */
BrowserAnimationBuilder.decorators;
/**
 * @nocollapse
 * @type {?}
 */
BrowserAnimationBuilder.ctorParameters;
/** @type {?} */
BrowserAnimationBuilder.prototype._nextAnimationId;
/** @type {?} */
BrowserAnimationBuilder.prototype._renderer;
}

export class BrowserAnimationFactory extends AnimationFactory {
/**
 * @param {?} _id
 * @param {?} _renderer
 */
constructor(private _id: string,
private _renderer: AnimationRenderer) { super(); }
/**
 * @param {?} element
 * @param {?=} options
 * @return {?}
 */
create(element: any, options?: AnimationOptions): AnimationPlayer {
    return new RendererAnimationPlayer(this._id, element, options || {}, this._renderer);
  }
}

function BrowserAnimationFactory_tsickle_Closure_declarations() {
/** @type {?} */
BrowserAnimationFactory.prototype._id;
/** @type {?} */
BrowserAnimationFactory.prototype._renderer;
}

export class RendererAnimationPlayer implements AnimationPlayer {
public parentPlayer: AnimationPlayer|null = null;
private _started = false;
/**
 * @param {?} id
 * @param {?} element
 * @param {?} options
 * @param {?} _renderer
 */
constructor(
public id: string,
public element: any, options: AnimationOptions,
private _renderer: AnimationRenderer) {
    this._command('create', options);
  }
/**
 * @param {?} eventName
 * @param {?} callback
 * @return {?}
 */
private _listen(eventName: string, callback: (event: any) => any): () => void {
    return this._renderer.listen(this.element, `@@${this.id}:${eventName}`, callback);
  }
/**
 * @param {?} command
 * @param {...?} args
 * @return {?}
 */
private _command(command: string, ...args: any[]) {
    return issueAnimationCommand(this._renderer, this.element, this.id, command, args);
  }
/**
 * @param {?} fn
 * @return {?}
 */
onDone(fn: () => void): void { this._listen('done', fn); }
/**
 * @param {?} fn
 * @return {?}
 */
onStart(fn: () => void): void { this._listen('start', fn); }
/**
 * @param {?} fn
 * @return {?}
 */
onDestroy(fn: () => void): void { this._listen('destroy', fn); }
/**
 * @return {?}
 */
init(): void { this._command('init'); }
/**
 * @return {?}
 */
hasStarted(): boolean { return this._started; }
/**
 * @return {?}
 */
play(): void {
    this._command('play');
    this._started = true;
  }
/**
 * @return {?}
 */
pause(): void { this._command('pause'); }
/**
 * @return {?}
 */
restart(): void { this._command('restart'); }
/**
 * @return {?}
 */
finish(): void { this._command('finish'); }
/**
 * @return {?}
 */
destroy(): void { this._command('destroy'); }
/**
 * @return {?}
 */
reset(): void { this._command('reset'); }
/**
 * @param {?} p
 * @return {?}
 */
setPosition(p: number): void { this._command('setPosition', p); }
/**
 * @return {?}
 */
getPosition(): number { return 0; }
public totalTime = 0;
}

function RendererAnimationPlayer_tsickle_Closure_declarations() {
/** @type {?} */
RendererAnimationPlayer.prototype.parentPlayer;
/** @type {?} */
RendererAnimationPlayer.prototype._started;
/** @type {?} */
RendererAnimationPlayer.prototype.totalTime;
/** @type {?} */
RendererAnimationPlayer.prototype.id;
/** @type {?} */
RendererAnimationPlayer.prototype.element;
/** @type {?} */
RendererAnimationPlayer.prototype._renderer;
}

/**
 * @param {?} renderer
 * @param {?} element
 * @param {?} id
 * @param {?} command
 * @param {?} args
 * @return {?}
 */
function issueAnimationCommand(
    renderer: AnimationRenderer, element: any, id: string, command: string, args: any[]): any {
  return renderer.setProperty(element, `@@${id}:${command}`, args);
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
